import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CircularProgressbar from 'react-circular-progressbar';
import { Spring, config } from 'react-spring/renderprops';
import NumberFormat from 'react-number-format';
import Pluralize from 'pluralize';
import './styles.css';
import RatingButtonMinimal from '../RatingButtonMinimal';

const RatingCircleWidgetWithButtons = props => {
  const {
    value = 0,
    calcPercentage = true,
    maxValue = 100,
    strokeWidth = 7,
    basedOn,
    basedOnNumber,
    basedOnHref,
    color,
    icon,
    disabled,
    mark,
    onChange,
    showButtons,
  } = props;

  const goodDealButtonHandler = () => {
    onChange(mark === 'Good' ? 0 : 1);
  };
  const badDealButtonHandler = () => {
    onChange(mark === 'Bad' ? 0 : -1);
  };

  const [count, text] = useMemo(
    () => [
      <NumberFormat
        thousandSeparator
        value={basedOnNumber}
        displayType={'text'}
      />,
      Pluralize(basedOn, basedOnNumber),
    ],
    [basedOnNumber, basedOn]
  );

  let svgIcon;
  let percentage;

  calcPercentage
    ? (percentage = (value / maxValue) * 100)
    : (percentage = value);

  const star = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 55.867 55.867"
      width="16"
      height="16"
    >
      <path
        d="M55.818 21.578c-.118-.362-.431-.626-.808-.681l-18.09-2.629-8.09-16.392c-.168-.342-.516-.558-.896-.558s-.729.216-.896.558l-8.091 16.393-18.09 2.629c-.377.055-.689.318-.808.681-.117.361-.02.759.253 1.024l13.091 12.76-3.091 18.018c-.064.375.09.754.397.978.309.226.718.255 1.053.076l16.182-8.506 16.18 8.506c.146.077.307.115.466.115.207 0 .413-.064.588-.191.308-.224.462-.603.397-.978l-3.09-18.017 13.091-12.761c.272-.267.37-.664.252-1.025z"
        fill={color}
      />
    </svg>
  );
  const thumbsUp = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 516 516"
      width="16"
      height="16"
    >
      <path
        d="M53.333 224C23.936 224 0 247.936 0 277.333V448c0 29.397 23.936 53.333 53.333 53.333h64c12.011 0 23.061-4.053 32-10.795V224h-96zM512 304c0-12.821-5.077-24.768-13.888-33.579 9.963-10.901 15.04-25.515 13.653-40.725-2.496-27.115-26.923-48.363-55.637-48.363H324.352c6.528-19.819 16.981-56.149 16.981-85.333 0-46.272-39.317-85.333-64-85.333-22.165 0-37.995 12.48-38.677 12.992-2.517 2.027-3.989 5.099-3.989 8.341v72.341l-61.44 133.099-2.56 1.301v228.651C188.032 475.584 210.005 480 224 480h195.819c23.232 0 43.563-15.659 48.341-37.269 2.453-11.115 1.024-22.315-3.861-32.043 15.765-7.936 26.368-24.171 26.368-42.688 0-7.552-1.728-14.784-5.013-21.333C501.419 338.731 512 322.496 512 304z"
        fill={color}
      />
    </svg>
  );
  const thumbsDown = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 516 516"
      width="16"
      height="16"
    >
      <path
        d="M53.333 288C23.936 288 0 264.064 0 234.667V64c0-29.397 23.936-53.333 53.333-53.333h64c12.011 0 23.061 4.053 32 10.795V288zM512 208c0 12.821-5.077 24.768-13.888 33.579 9.963 10.901 15.04 25.515 13.653 40.725-2.496 27.115-26.923 48.363-55.637 48.363H324.352c6.528 19.819 16.981 56.149 16.981 85.333 0 46.272-39.317 85.333-64 85.333-22.165 0-37.995-12.48-38.677-12.992-2.517-2.027-3.989-5.099-3.989-8.341v-72.341l-61.44-133.099-2.56-1.301V44.608C188.032 36.416 210.005 32 224 32h195.819c23.232 0 43.563 15.659 48.341 37.269 2.453 11.115 1.024 22.315-3.861 32.043 15.765 7.936 26.368 24.171 26.368 42.688 0 7.552-1.728 14.784-5.013 21.333C501.419 173.269 512 189.504 512 208z"
        fill={color}
      />
    </svg>
  );

  switch (icon) {
    case 'like':
      svgIcon = thumbsUp;
      break;
    case 'dislike':
      svgIcon = thumbsDown;
      break;
    default:
      svgIcon = star;
      break;
  }

  return (
    <div className="rating-circle-widget-with-btn">
      <div className="rating-circle-widget-with-btn__wrapper">
        <div className="rating-circle-widget-with-btn__inner">
          <span className="rating-circle-widget-with-btn__icon">{svgIcon}</span>
          <div
            className="rating-circle-widget-with-btn__number"
            style={{ color: `${color}` }}
          >
            {value}
            {icon === 'like' || icon === 'dislike' ? '%' : ''}
          </div>
        </div>

        <Spring
          config={config.slow}
          from={{ value: 0 }}
          to={{ value: percentage }}
        >
          {props => (
            <CircularProgressbar
              styles={{
                path: {
                  stroke: `${color}`,
                },
              }}
              strokeWidth={strokeWidth}
              percentage={props.value}
              text={value}
            />
          )}
        </Spring>
      </div>

      {basedOnHref ? (
        <a
          className="rating-circle-widget-with-btn__text rating-circle-widget-with-btn__text--link"
          href={basedOnHref}
        >
          {`based on ${count} ${text}`}
        </a>
      ) : (
        <div className="rating-circle-widget-with-btn__text">
          <span className="rating-circle-widget-with-btn__text-line">
            based on {count} {text}
          </span>

          {showButtons && (
            <RatingButtonMinimal
              caption={icon === 'like' ? 'Good Deal' : 'Bad Deal'}
              icon={icon}
              disabled={disabled}
              action={
                icon === 'like' ? goodDealButtonHandler : badDealButtonHandler
              }
              toggled={icon === 'like' ? mark === 'Good' : mark === 'Bad'}
            />
          )}
        </div>
      )}
    </div>
  );
};

RatingCircleWidgetWithButtons.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  maxValue: PropTypes.number.isRequired,
  icon: PropTypes.oneOf(['like', 'dislike', 'star']).isRequired,
  basedOn: PropTypes.string.isRequired,
  basedOnNumber: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  strokeWidth: PropTypes.number,
  basedOnHref: PropTypes.string,
  calcPercentage: PropTypes.bool,
};

export default RatingCircleWidgetWithButtons;
